import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  StatusDot,
  Table,
  Text,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  useCreateMcpTokenMutation,
  useGetApplicationsQuery,
  useGetMcpAuditQuery,
  useGetMcpTokensQuery,
  useRevokeMcpTokenMutation,
  type McpAuditEvent,
  type McpScope,
  type McpToken,
} from '../../store/api';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 960px;
  margin: 0 auto;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const TokenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TokenRow = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
`;

const TokenMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.35rem;
`;

const ScopeRow = styled.label`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  font-size: 0.875rem;
`;

const SecretBox = styled.pre`
  padding: 0.75rem 1rem;
  background: var(--bg-elevated, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-all;
`;

const ALL_SCOPES: { id: McpScope; label: string; hint: string }[] = [
  { id: 'agents:invoke', label: 'Invoke', hint: 'List, get, and run agents' },
  { id: 'agents:write', label: 'Write', hint: 'Create and edit drafts' },
  { id: 'agents:publish', label: 'Publish', hint: 'Publish a draft (confirm required)' },
];

function remoteSnippet(): string {
  return JSON.stringify(
    {
      mcpServers: {
        'luminafactory-dev': {
          url: 'https://api.dev.luminafactory.com/mcp',
        },
      },
    },
    null,
    2,
  );
}

function snippetFor(token: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        'luminafactory-dev': {
          command: 'python3',
          args: ['-m', 'lumina_mcp'],
          cwd: 'tealslumina/lumina-mcp',
          env: {
            LUMINA_API_BASE_URL: 'https://api.dev.luminafactory.com',
            LUMINA_MCP_TOKEN: token,
          },
        },
      },
    },
    null,
    2,
  );
}

const Mcp: React.FC = () => {
  const { data: tokens = [], isLoading } = useGetMcpTokensQuery();
  const { data: apps = [] } = useGetApplicationsQuery();
  const { data: audit = [] } = useGetMcpAuditQuery();
  const [createToken, { isLoading: creating }] = useCreateMcpTokenMutation();
  const [revokeToken] = useRevokeMcpTokenMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [scopes, setScopes] = useState<McpScope[]>(['agents:invoke']);
  const [revealed, setRevealed] = useState<McpToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<McpToken | null>(null);
  const [error, setError] = useState<string | null>(null);

  const appName = useMemo(() => {
    const map: Record<string, string> = {};
    apps.forEach((app) => {
      map[app.appId] = app.name;
    });
    return map;
  }, [apps]);

  const auditColumns: Column<McpAuditEvent>[] = [
    { key: 'toolName', header: 'Tool', render: (_v, row) => row.toolName || row.operation },
    { key: 'agentName', header: 'Agent', render: (_v, row) => row.agentName || '—' },
    { key: 'resultStatus', header: 'Status' },
    { key: 'createdAt', header: 'Time', render: (_v, row) => row.createdAt || '—' },
  ];

  const toggleScope = (scope: McpScope) => {
    if (scope === 'agents:invoke') return;
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope],
    );
  };

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      const created = await createToken({
        name: name.trim(),
        applicationId,
        scopes,
      }).unwrap();
      setCreateOpen(false);
      setName('');
      setRevealed(created);
    } catch {
      setError('Could not create token.');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    await revokeToken(revokeTarget.tokenId).unwrap();
    setRevokeTarget(null);
  };

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  return (
    <Page>
      <Header>
        <div>
          <Text variant="h2">MCP</Text>
          <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
            Connect Cursor as you via OAuth, or mint a stdio PAT. Distinct from Direct
            API keys in Business Center. Revoke any grant immediately.
          </Text>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Mint PAT</Button>
      </Header>

      <TokenRow>
        <div>
          <Text variant="h6">Connect with OAuth</Text>
          <Text variant="body2" style={{ color: 'var(--text-muted)' }}>
            In Cursor, add a remote MCP URL. Cursor opens this portal for consent — no
            PAT in git.
          </Text>
        </div>
        <Button
          variant="secondary"
          onClick={() => copy(remoteSnippet())}
        >
          Copy remote mcp.json
        </Button>
      </TokenRow>

      {isLoading ? (
        <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
          Loading grants…
        </Text>
      ) : tokens.length === 0 ? (
        <EmptyState
          title="No MCP grants yet"
          description="Connect Cursor with OAuth (recommended) or mint a stdio PAT."
          action={<Button onClick={() => setCreateOpen(true)}>Mint PAT</Button>}
        />
      ) : (
        <TokenList>
          {tokens.map((token) => (
            <TokenRow key={token.tokenId}>
              <div>
                <Text variant="h6">{token.name}</Text>
                <TokenMeta>
                  <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
                    {token.clientId || 'cursor'} · {token.userId}
                  </Text>
                  <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
                    {appName[token.applicationId || ''] ||
                      (token.appIds && token.appIds.length
                        ? token.appIds.join(', ')
                        : 'All agents')}
                  </Text>
                  {(token.scopes.length ? token.scopes : token.capabilities || []).map(
                    (scope) => (
                    <Badge key={scope} variant="neutral">
                      {scope.replace('agents:', '')}
                    </Badge>
                    ),
                  )}
                  <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
                    {token.tokenSuffix ? `…${token.tokenSuffix}` : token.createdFrom || 'oauth'}
                  </Text>
                  <StatusDot
                    status={token.status === 'active' ? 'active' : 'draft'}
                    size="sm"
                    label={token.status}
                  />
                  <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
                    {token.lastUsedAt ? `last used ${token.lastUsedAt}` : ''}
                  </Text>
                </TokenMeta>
              </div>
              {token.status === 'active' && (
                <Button variant="secondary" onClick={() => setRevokeTarget(token)}>
                  Revoke
                </Button>
              )}
            </TokenRow>
          ))}
        </TokenList>
      )}

      <div>
        <Text variant="h4">Recent audit</Text>
        {audit.length === 0 ? (
          <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
            No MCP calls yet.
          </Text>
        ) : (
          <Table columns={auditColumns} data={audit} rowKey="auditId" />
        )}
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Mint stdio PAT"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <Text variant="body2" style={{ color: 'var(--text-muted)' }}>
            This token acts as you. Store it like a password. The secret is shown once.
          </Text>
          <Input label="Name" value={name} onChange={(e: any) => setName(e.target.value)} />
          <label>
            <Text variant="h6">Application (optional)</Text>
            <select
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              style={{ width: '100%', marginTop: '0.35rem', padding: '0.5rem' }}
            >
              <option value="">All agents</option>
              {apps.map((app) => (
                <option key={app.appId} value={app.appId}>
                  {app.name}
                </option>
              ))}
            </select>
          </label>
          {ALL_SCOPES.map((scope) => (
            <ScopeRow key={scope.id}>
              <input
                type="checkbox"
                checked={scopes.includes(scope.id)}
                disabled={scope.id === 'agents:invoke'}
                onChange={() => toggleScope(scope.id)}
              />
              <span>
                <strong>{scope.label}</strong> — {scope.hint}
              </span>
            </ScopeRow>
          ))}
          {error ? (
            <Text variant="body2" style={{ color: 'var(--danger, #dc2626)' }}>
              {error}
            </Text>
          ) : null}
          <Button onClick={handleCreate} disabled={creating}>
            Mint PAT
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(revealed?.token)}
        onClose={() => setRevealed(null)}
        title="Copy your token now"
      >
        {revealed?.token ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <Text variant="body2">
              You cannot view this secret again. Paste it into Cursor MCP env as
              LUMINA_MCP_TOKEN.
            </Text>
            <SecretBox data-testid="mcp-secret">{revealed.token}</SecretBox>
            <Button onClick={() => copy(revealed.token || '')}>Copy token</Button>
            <SecretBox data-testid="mcp-snippet">{snippetFor(revealed.token)}</SecretBox>
            <Button variant="secondary" onClick={() => copy(snippetFor(revealed.token || ''))}>
              Copy Cursor snippet
            </Button>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title="Revoke token?"
      >
        {revokeTarget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <Text>
              Revoking {revokeTarget.name} immediately stops Cursor (and any other MCP
              client) using …{revokeTarget.tokenSuffix}.
            </Text>
            <Button variant="danger" onClick={handleRevoke}>
              Revoke
            </Button>
          </div>
        ) : null}
      </Modal>
    </Page>
  );
};

export default Mcp;
